import { PaginatedResponse } from "@core/types";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  communicationApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { DropdownItem } from "@shared/api/types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  Communication,
  CommunicationFilters,
  CommunicationPayload,
} from "../shared/types/communicationTypes";
import {
  ConsumerMetaData,
  ConsumerSearchResult,
  Note,
  PaymentMethod,
  TimeSlotChoice,
} from "../shared/types/consumerRequest";
import { CreateNoteInput } from "../shared/types/notesTabProps";
import {
  CreateServiceRequestPayload,
  NoteFilters,
  NoteTypeChoice,
  ServiceRequestDetail,
  ServiceRequestFilters,
  ServiceRequestListResponse,
  ServiceRequestSummary,
  TransactionStatusChoice,
  UtilityConfigFilters,
  UtilityRequestConfiguration,
} from "./types";

// API Functions
export const serviceRequestApi = {
  // Consumer Search
  searchConsumer: async (params: {
    remoteUtilityId: number;
    requestType: string;
    searchData: string;
  }): Promise<{ result: ConsumerSearchResult[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-search/info",
      (query) => {
        query.push("remote_utility_id", getRemoteUtilityId());
        query.push("request_type", params.requestType);
        query.push("search_data", params.searchData);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerSearchResult[] }>(
      response.data
    );
  },

  // Consumer Meta Data
  getConsumerMetaData: async (params: {
    consumerId: number;
    remoteUtilityId: number;
  }): Promise<{ result: ConsumerMetaData }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-meta-data",
      (query) => {
        query.push("consumer_id", params.consumerId.toString());
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerMetaData }>(response.data);
  },

  // Utility Request Configuration
  getUtilityRequestConfiguration: async (
    params: UtilityConfigFilters
  ): Promise<{ result: UtilityRequestConfiguration[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "utility-request-configuration",
      (query) => {
        if (params.remoteUtilityId)
          query.push("remote_utility_id", params.remoteUtilityId.toString());
        if (params.requestType) query.push("request_type", params.requestType);
        if (params.disablePagination) query.push("disable_pagination", "true");
        if (params.utilityService?.length) {
          params.utilityService.forEach((service) =>
            query.push("utility_service", service)
          );
        } else if (params.productCode?.length) {
          params.productCode.forEach((code) =>
            query.push("utility_service", code)
          );
        }
        if (params.searchData) query.push("search_data", params.searchData);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: UtilityRequestConfiguration[] }>(
      response.data
    );
  },

  // Choice APIs
  getTimeSlotChoices: async (): Promise<{ result: TimeSlotChoice[] }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/time_slot");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: TimeSlotChoice[] }>(response.data);
  },

  getTransactionStatusChoices: async (): Promise<{
    result: TransactionStatusChoice[];
  }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/transaction_status");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: TransactionStatusChoice[] }>(
      response.data
    );
  },

  getNoteTypeChoices: async (): Promise<{ result: NoteTypeChoice[] }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/note_type");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: NoteTypeChoice[] }>(response.data);
  },

  // Service Request List
  getServiceRequests: async (
    filters: ServiceRequestFilters
  ): Promise<ServiceRequestListResponse> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-service-request",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.requestType)
          query.push("request_type", filters.requestType);
        if (filters.activeTab) query.push("active_tab", filters.activeTab);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status) query.push("status", filters.status);
        if (filters.name) query.push("name", filters.name);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<ServiceRequestListResponse>(response.data);
  },

  exportServiceRequests: async (filters: ServiceRequestFilters): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-service-request",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.requestType)
          query.push("request_type", filters.requestType);
        if (filters.activeTab) query.push("active_tab", filters.activeTab);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status) query.push("status", filters.status);
        if (filters.name) query.push("name", filters.name);
        query.push("export_format", "csv");
        return query;
      }
    );
  
    const response = await cxApiClient.get(url);

    return response.data;
  },

  // Service Request Detail
  getServiceRequestDetail: async (
    requestId: number
  ): Promise<{ result: ServiceRequestDetail }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-service-request/detail",
      (query) => {
        query.push("request_id", requestId.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ServiceRequestDetail }>(
      response.data
    );
  },

  // Update Service Request
  updateServiceRequest: async (
    requestId: number,
    payload: any
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer-service-request",
      [requestId.toString()]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  // Service Request Summary
  getServiceRequestSummary: async (params: {
    remoteUtilityId: number;
    requestType: string;
  }): Promise<{ result: ServiceRequestSummary }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-service-request/summary",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("request_type", params.requestType);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ServiceRequestSummary }>(
      response.data
    );
  },

  // Create Service Request
  createServiceRequest: async (
    payload: CreateServiceRequestPayload
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "consumer-service-request");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  // Service Request Notes
  createServiceRequestNote: async (
    payload: CreateNoteInput
  ): Promise<{ result: Note[] }> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "consumer-request",
      ["notes"]
    );
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<{ result: Note[] }>(response.data);
  },

  getServiceRequestNotes: async (
    filters: NoteFilters
  ): Promise<{ result: Note[] }> => {
    // ✅ Early check to prevent unnecessary API call
    if (!filters.remoteUtilityId || !filters.requestId) {
      return { result: [] };
    }

    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-request",
      ["notes"],
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.requestId)
          query.push("request_id", filters.requestId.toString());
        if (filters.noteType) query.push("note_type", filters.noteType);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: Note[] }>(response.data);
  },

  getServiceRequestActivities: async (
    filters: NoteFilters
  ): Promise<{ result: Note[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-request",
      ["activity-logs"],
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.requestId)
          query.push("request_id", filters.requestId.toString());
        if (filters.noteType) query.push("note_type", filters.noteType);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: Note[] }>(response.data);
  },

  getStatus: async (params: {
    remoteUtilityId: number;
    tab: number;
  }): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["consumer_support_request_status"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("tab", params.tab);
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getCategory: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: any[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "utility-request-configuration",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("request_type", "Service");
        query.push("disable_pagination", true);
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return response?.data.result.map((item) => ({
      label: item.name,
      value: item.name,
    }));
  },

  getPaymentMethod: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: PaymentMethod[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["payment_mode"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      }
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<{ result: PaymentMethod[] }>(response.data);
  },
  getCommunications: async (
    filters: CommunicationFilters
  ): Promise<PaginatedResponse<Communication>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "notifications",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("page", filters.page.toString());
        query.push("limit", filters.limit.toString());
        query.push("type", filters.communicationType);
        // query.push('date_range', filters.dateRange);
        query.push("entity_id", filters.entityId.toString());
        query.push("action_type", filters.actionType);
        return query;
      }
    );
    const response = await communicationApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<Communication>>(
      response.data
    );
  },
  getChannelType: async (): Promise<DropdownItem[]> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "communication",
      "choices",
      ["channel_type"]
    );
    const response = await communicationApiClient.get(url);
    return response.data.result;
  },
  getChannelTemplates: async (
    channelType: string,
    remoteUtilityId: number
  ): Promise<DropdownItem[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "template-dropdown",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
        qs.push("channel_type", channelType);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data.result;
  },
  sendCommunication: async (
    data: CommunicationPayload
  ): Promise<CommunicationPayload> => {
    const response = await communicationApiClient.post(
      ApiEndpoints.createUrl("cx", "update-response-time"),
      data
    );
    return response.data;
  },
};
